import { useEffect, useState } from 'preact/hooks'
import { contactDetailApi, type ContactAnchorType, type ContactDetailDto } from '@/api/contactDetail'
import { useContactTabRouter, type ContactTabKey } from '@/hooks/useContactTabRouter'
import { usePageHeader } from '@/hooks/usePageHeader'
import { useEnrichmentEvent } from '@/hooks/useEnrichmentEvent'
import { useToast } from '@/hooks/useToast'
import { useUserProfile } from '@/hooks/useUserProfile'
import { routes } from '@/utils/routes'
import { audienceApi } from '@/api/audience'
import { PersonAvatar } from '@/components/common/Avatar'
import { SignalBars } from '@/components/common/SignalBars'
import { CopyButton } from '@/components/common/CopyButton'
import { LinkedInGlyph } from '@/components/companies/companyGlyphs'
import { toSafeUrl } from '@/components/companies/companyLinks'
import { ConsentChip, ScopeBadge, Icon } from './contactShared'
import { ICONS, quizTabHrefFor } from './contactFormat'
import { ContactOverviewTab } from './tabs/ContactOverviewTab'
import { ContactQuizTab } from './tabs/ContactQuizTab'
import { ContactActivityTab } from './tabs/ContactActivityTab'
import { ContactEmailsTab } from './tabs/ContactEmailsTab'
import { ContactProfileTab } from './tabs/ContactProfileTab'

interface Props {
  clientCompanyId: string
  workspaceId: string
  /** Absent => cross-campaign (workspace) scope: view-only Overview + Profile. */
  campaignId?: string
  webId?: string
  anchorType: ContactAnchorType
  anchorId: string
}

export function ContactDetailPage({ clientCompanyId, workspaceId, campaignId, webId, anchorType, anchorId }: Props) {
  const { setHeader } = usePageHeader()
  const { getWorkspaceCapabilities } = useUserProfile()
  const { activeTab, routeToTab } = useContactTabRouter({ clientCompanyId, workspaceId, campaignId, webId, anchorType, anchorId })
  const [detail, setDetail] = useState<ContactDetailDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    setLoading(true)
    setError(null)
    const ctrl = new AbortController()
    // Workspace (cross-campaign) scope takes either anchor since ORB-623: a lead by its
    // representative id, or an audience row for someone targeted who never engaged.
    const request = campaignId
      ? contactDetailApi.getDetail(workspaceId, campaignId, anchorType, anchorId, webId, ctrl.signal)
      : contactDetailApi.getWorkspaceDetail(workspaceId, anchorType, anchorId, ctrl.signal)
    request
      .then(res => {
        if (ctrl.signal.aborted) return
        if (res.data) setDetail(res.data)
        else setError(res.error || 'Contact not found')
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
  }, [workspaceId, campaignId, anchorType, anchorId, webId, refreshKey])

  // Live refresh when this contact's enrichment finishes (they may have kicked it off here).
  useEnrichmentEvent((evt) => {
    const matches =
      (anchorType === 'lead' && evt.leadId === anchorId) ||
      (anchorType === 'audience' && evt.fileImportRowId === anchorId) ||
      (!!detail?.ContactId && evt.contactId === detail.ContactId)
    if (matches) setRefreshKey(k => k + 1)
  })

  useEffect(() => {
    const scoped = routes.scoped(clientCompanyId, workspaceId)
    setHeader({
      items: [
        ...(campaignId
          ? [
              { text: 'Campaigns', href: scoped.campaignsIndex() },
              { text: 'Campaign', href: scoped.campaign(campaignId) },
              ...(webId ? [{ text: 'Web', href: scoped.web(campaignId, webId) }] : []),
            ]
          : [{ text: 'People', href: scoped.section('leads') }]),
        { text: detail?.FullName || 'Contact' },
      ],
    })
  }, [clientCompanyId, workspaceId, campaignId, webId, detail?.FullName])

  if (loading && !detail) {
    return (
      <div class="p-6 flex items-center gap-3 text-slate-600 dark:text-slate-300">
        <div class="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        Loading contact…
      </div>
    )
  }
  if (error && !detail) return <div class="p-6 text-red-600 dark:text-red-400">{error}</div>
  if (!detail) return null

  // Workspace (cross-campaign) scope is view-only: Activity/Emails are reached by drilling a touchpoint
  // into a campaign-scoped view, so only Overview + Profile show there.
  // The Quiz tab needs the web: a lead page opened inside a web where this person has a quiz submission,
  // including one still awaiting answers.
  const quiz = campaignId && webId && detail.LeadId ? detail.Quiz : null
  const tabs: Array<{ key: ContactTabKey; label: string }> = campaignId
    ? [
        { key: 'overview', label: 'Overview' },
        ...(quiz ? [{ key: 'quiz' as const, label: 'Quiz' }] : []),
        { key: 'activity', label: 'Activity' },
        { key: 'emails', label: 'Emails' },
        { key: 'profile', label: 'Profile' },
      ]
    : [
        { key: 'overview', label: 'Overview' },
        { key: 'profile', label: 'Profile' },
      ]

  // The active tab can come from a stale or deep-linked URL segment that isn't valid at this scope
  // (e.g. 'activity' at the workspace level, where only Overview + Profile exist). Clamp it to a tab
  // that actually renders, so the body never goes blank with nothing selected in the nav.
  const effectiveTab = tabs.some(t => t.key === activeTab) ? activeTab : 'overview'

  const onPatch = (patch: Partial<ContactDetailDto>) => setDetail(prev => (prev ? { ...prev, ...patch } : prev))
  const quizHref = quizTabHrefFor(detail)

  // Attribution is stored per Web, so it is only editable in web scope — and only on a lead, since
  // the endpoint writes to the lead and takes the lead's ETag (ORB-608). Assert the anchor rather than
  // inferring it from LeadId, matching ContactProfileTab: the API leaves LeadId null on an audience
  // anchor today, but that is its invariant to keep, not one this gate should depend on.
  const isLeadAnchor = detail.AnchorType === 'Lead' && !!detail.LeadId
  const editWebSource =
    campaignId && webId && isLeadAnchor && getWorkspaceCapabilities(detail.ClientCompanyId, workspaceId).canEdit
      ? { workspaceId, campaignId, webId, onPatch }
      : undefined

  return (
    <div class="p-6">
      <div class="mx-auto max-w-6xl">
        <div class="mb-4 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span class="font-medium text-slate-700 dark:text-slate-200">{detail.FullName || detail.Email || 'Contact'}</span>
          <ScopeBadge scope={detail.Scope} />
        </div>

        <section class="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <ContactHeader
            detail={detail}
            workspaceId={workspaceId}
            campaignId={campaignId}
            onPatch={onPatch}
          />

          {/* cross-scope nav: only render links that resolve today (campaign scope from a web slice) */}
          {webId && campaignId && (
            <div class="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-slate-100 dark:border-slate-800/60 px-6 py-2.5 text-xs text-slate-500 dark:text-slate-400">
              <Icon path={ICONS.arrowRight} size={13} />
              See <span class="font-medium text-slate-600 dark:text-slate-300">{firstName(detail.FullName)}</span> across
              <a
                href={routes.scoped(clientCompanyId, workspaceId).campaignContact(campaignId, anchorType, anchorId)}
                class="font-medium text-primary-700 dark:text-primary-400 hover:underline"
              >
                this campaign
              </a>
            </div>
          )}

          <div class="border-y border-slate-200 dark:border-slate-800 px-4">
            <nav class="-mb-px flex gap-1" aria-label="Contact sections">
              {tabs.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => routeToTab(t.key)}
                  class={`relative px-3 py-2.5 text-sm font-medium ${effectiveTab === t.key ? 'text-primary-700 dark:text-primary-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  {t.label}
                  <span class={`absolute inset-x-3 bottom-0 h-0.5 rounded-full ${effectiveTab === t.key ? 'bg-primary-600' : 'bg-transparent'}`} />
                </button>
              ))}
            </nav>
          </div>

          <div>
            {effectiveTab === 'overview' && (
              <ContactOverviewTab detail={detail} onGoTab={routeToTab} canViewActivity={!!campaignId} editWebSource={editWebSource} quizHref={quizHref} />
            )}
            {effectiveTab === 'quiz' && quiz && campaignId && webId && detail.LeadId && (
              <ContactQuizTab quiz={quiz} workspaceId={workspaceId} campaignId={campaignId} leadId={detail.LeadId} webId={webId} />
            )}
            {effectiveTab === 'activity' && campaignId && (
              <ContactActivityTab
                workspaceId={workspaceId}
                campaignId={campaignId}
                anchorType={anchorType}
                anchorId={anchorId}
                isAudienceOnly={detail.IsAudienceOnly}
                quizHref={quizHref}
              />
            )}
            {effectiveTab === 'emails' && campaignId && (
              <ContactEmailsTab workspaceId={workspaceId} campaignId={campaignId} anchorType={anchorType} anchorId={anchorId} />
            )}
            {effectiveTab === 'profile' && (
              <ContactProfileTab detail={detail} workspaceId={workspaceId} campaignId={campaignId} viewOnly={!campaignId} onPatch={onPatch} />
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

function firstName(fullName?: string | null): string {
  return (fullName || '').trim().split(/\s+/)[0] || 'this contact'
}

interface HeaderProps {
  detail: ContactDetailDto
  workspaceId: string
  campaignId?: string
  onPatch: (patch: Partial<ContactDetailDto>) => void
}

function ContactHeader({ detail, workspaceId, campaignId, onPatch }: HeaderProps) {
  const { showError } = useToast()
  const [enriching, setEnriching] = useState(false)
  const isLead = !detail.IsAudienceOnly

  const pending = detail.Enrichment.Status === 'Pending'
  // Person enrichment is keyed on the LinkedIn URN, so a row that arrived without one has
  // nothing to look up and the call would return it unchanged (ORB-834).
  const canEnrich = detail.Enrichment.CanEnrich
  const linkedinUrl = toSafeUrl(detail.LinkedinProfileUrl)

  // Offered only to an audience-only contact, which the API always anchors on its file import row.
  const enrich = async () => {
    if (enriching || pending || !canEnrich || !campaignId || !detail.FileImportRowId) return
    setEnriching(true)
    const res = await audienceApi.reEnrich(detail.ClientCompanyId, workspaceId, campaignId, detail.FileImportRowId)
    setEnriching(false)
    if (res.error) { showError('Enrichment failed', res.error); return }
    onPatch({ Enrichment: { ...detail.Enrichment, Status: 'Pending' } })
  }

  return (
    <header class="flex flex-wrap items-start gap-4 px-6 pt-6 pb-5">
      <PersonAvatar firstName={detail.FirstName} lastName={detail.LastName} fullName={detail.FullName} profilePicUrl={detail.ProfilePicUrl} size="xl" />

      <div class="min-w-[260px] flex-1">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 class="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">{detail.FullName || detail.Email || 'Unknown contact'}</h1>
          <ConsentChip state={detail.Consent.State} big />
        </div>

        <p class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-slate-500 dark:text-slate-400">
          {detail.JobTitle && <span>{detail.JobTitle}</span>}
          {detail.JobTitle && detail.CompanyName && <span class="text-slate-300 dark:text-slate-600">·</span>}
          {detail.CompanyName && <span>{detail.CompanyName}</span>}
          {detail.Email && (
            <>
              <span class="text-slate-300 dark:text-slate-600">·</span>
              <span class="inline-flex items-center gap-1">
                <span class="text-slate-600 dark:text-slate-300">{detail.Email}</span>
                <CopyButton text={detail.Email} />
              </span>
            </>
          )}
        </p>

        {/* adaptive hero */}
        {isLead ? (
          <div class="mt-3 flex items-center gap-2.5">
            <SignalBars score={detail.SignalScore ?? 0} class="flex items-end h-6 w-10 gap-0.5" />
          </div>
        ) : (
          <div class="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span class="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
              <Icon path={ICONS.target} />
              Targeted{detail.TargetingListName ? <> · <span class="font-medium text-slate-800 dark:text-slate-200">{detail.TargetingListName}</span></> : null}
            </span>
            {detail.TargetingReason && (
              <span class="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300">{detail.TargetingReason}</span>
            )}
          </div>
        )}

        {/* facet chips */}
        <div class="mt-3 flex flex-wrap items-center gap-1.5">
          <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500 mr-0.5">Facets</span>
          <FacetChip label="Lead" on={detail.Facets.IsLead} lead />
          <FacetChip label="Audience" on={detail.Facets.IsAudience} />
          <FacetChip label="Visitor" on={detail.Facets.IsVisitor} />
        </div>
      </div>

      {/* primary action adapts to state */}
      <div class="flex shrink-0 items-center gap-2">
        {isLead && linkedinUrl && (
          <a
            href={linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View profile on LinkedIn"
            class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
          >
            <LinkedInGlyph class="w-3.75 h-3.75 text-[#0a66c2]" />
            View profile
            <Icon path={ICONS.external} size={13} strokeWidth={2.2} class="text-slate-400 dark:text-slate-500" />
          </a>
        )}
        {isLead ? (
          <button
            type="button"
            disabled
            title="Coming soon"
            class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm font-medium text-slate-500 dark:text-slate-400 cursor-not-allowed"
          >
            <Icon path={ICONS.mail} size={15} />
            Compose email
            <span class="ml-0.5 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Coming soon</span>
          </button>
        ) : !canEnrich ? null : (
          // Enrichment needs a campaign to run in, and workspace scope has none. Since ORB-623
          // lets an audience person be opened at workspace scope, this branch is reachable there
          // for the first time — and `enrich` bails on a missing campaignId in silence. Say so on
          // the button rather than offering an action that does nothing.
          //
          // A row with no URN drops the button entirely instead, because that is what the profile
          // tab and the peek flyout do for the same person. Disabling it here was defensible on its
          // own and wrong across the three together: the same contact appeared to be handled three
          // different ways, and a greyed button still reads as an offer (ORB-834).
          <button
            type="button"
            onClick={enrich}
            disabled={enriching || pending || !campaignId}
            title={!campaignId ? 'Enrichment is available from a campaign-scoped view' : undefined}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60 disabled:cursor-default focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
          >
            <Icon path={ICONS.sparkle} size={15} />
            {enriching || pending ? 'Enriching…' : 'Enrich contact'}
          </button>
        )}
      </div>
    </header>
  )
}

function FacetChip({ label, on, lead }: { label: string; on: boolean; lead?: boolean }) {
  const cls = on
    ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200'
    : 'text-slate-400 dark:text-slate-600 line-through decoration-slate-300 dark:decoration-slate-700'
  const dot = lead && on ? 'bg-purple-500' : on ? 'bg-slate-400' : 'bg-slate-300 dark:bg-slate-700'
  return (
    <span class={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium ${cls}`}>
      <span class={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  )
}
